import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import Input from "@components/core/Input";
import { useDeliveryCountries } from "@hooks/use-features";
import classNames from "classnames";

const AddressesSearchBar = ({
  placeholder,
  onAddressSelected,
  loadGoogleMapsScript = false,
  limitToCountry = null,
  error = "",
  hidden = false,
}) => {
  const t = useTranslations("address_form");

  const deliveryCountries = useDeliveryCountries();
  const [inputError, setInputError] = useState("");
  const autocompleteRef = useRef(null);
  const autocompleteListenerRef = useRef(null);

  function getCountryFromPlace(place) {
    if (!place?.address_components) return null;

    const countryComponent = place.address_components.find((c) => c.types.includes("country"));
    return countryComponent ? countryComponent.short_name : null;
  }

  function initAutocomplete() {
    if (autocompleteListenerRef.current) autocompleteListenerRef.current.remove();

    const input = document.getElementById("pac-input");
    const options = {
      componentRestrictions: { country: limitToCountry },
      types: ["geocode"],
    };
    // eslint-disable-next-line no-undef
    const autocomplete = new google.maps.places.Autocomplete(input, options);
    autocompleteRef.current = autocomplete;
    autocomplete.setFields(["address_component", "geometry", "formatted_address"]);
    autocompleteListenerRef.current = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry) return;

      const country = getCountryFromPlace(place);
      if (!deliveryCountries.includes(country)) {
        setInputError(t("delivery_country_not_supported"));
        return;
      }
      setInputError("");
      onAddressSelected(place);
      const input = document.getElementById("pac-input");
      input.value = "";
    });
  }

  useEffect(() => {
    function addGoogleMapsScript() {
      if (!window.google?.maps && loadGoogleMapsScript) {
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.src = `https://maps.google.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY}&libraries=geometry,places`;
        const x = document.getElementsByTagName("script")[0];
        x.parentNode.insertBefore(s, x);
      }
    }

    addGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  return (
    <Input
      id="pac-input"
      type="text"
      className={classNames("mb-0", hidden && "d-none")}
      placeholder={placeholder}
      onFocus={() => initAutocomplete()}
      onKeyDown={(e) => {
        if (e.keyCode === 13) {
          e.preventDefault();
        }
      }}
      error={inputError || error}
    />
  );
};

export default AddressesSearchBar;
